import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import { UserRole, CommunicationLanguage, RegistrationType, MatchMemberRequest } from '../types/auth';
import type { IndividualMemberDto } from '../types/member';
import { MembershipType, getMemberDisplayFirstName, getMemberDisplayLastName } from '../types/member';
import { authService } from '../services/auth';
import { getApiErrorMessage } from '../utils/apiErrors';
import FormField from '../components/common/FormField';
import PasswordVisibilityToggle from '../components/common/PasswordVisibilityToggle';
import { Stepper, StepState } from '../design-system/components/Stepper';
import { Button } from '../design-system/components/Button';
import { Card } from '../design-system/components/Card';
import { Heading, Text } from '../design-system/components/Typography';
import { FormSection } from '../design-system/patterns/FormSection';
import {
  Form,
  CheckboxContainer,
  Checkbox,
  CheckboxLabel,
} from '../design-system/patterns/FormLayout';
import { Stack } from '../design-system/components/Layout';
import { Badge } from '../design-system/components/Decorative';
import AuthWizardShell from '../components/registration/AuthWizardShell';
import WizardStepPanel from '../components/registration/WizardStepPanel';
import { spacing } from '../design-system/tokens/spacing';
import { typography } from '../design-system/tokens/typography';
import { hexToRgba } from '../utils/colorUtils';

const ErrorMessage = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.error, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.error};
  margin-bottom: ${spacing[6]};
`;

const SuccessMessage = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.success, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.success};
  margin-bottom: ${spacing[6]};
`;

const WarningMessage = styled(Card)`
  padding: ${spacing[4]};
  background: ${props => hexToRgba(props.theme.colors.warning, 0.1)};
  border-left: 4px solid ${props => props.theme.colors.warning};
  margin-bottom: ${spacing[6]};
`;

const MemberInfoCard = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const BackLink = styled(Link)`
  color: ${props => props.theme.colors.primary};
  text-decoration: none;
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  font-family: ${typography.fontFamily.primary};
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[4]};
  transition: color 0.2s ease;
  &:hover {
    color: ${props => props.theme.colors.primaryDark};
    text-decoration: underline;
  }
`;

const CardWithPadding = styled(Card)`
  padding: ${spacing[6]};
`;

const ButtonGroupRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: ${spacing[4]};
`;

const NavSpacer = styled.span`
  display: inline-block;
  min-width: 1px;
`;

const HeadingWithMargin = styled(Heading)`
  margin-bottom: ${spacing[2]};
`;

const TextWithMargin = styled(Text)`
  margin-bottom: ${spacing[2]};
`;

const TextUppercase = styled(Text)`
  letter-spacing: 0.05em;
`;

const StepperWrapper = styled.div`
  margin-bottom: ${spacing[4]};
  padding: ${spacing[4]} 0;
`;

const RegisterExistingMember: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  const { success: showSuccess, error: showError } = useToast();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  // Step 1: Member Information (Greek name + Member Card Number)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [memberCardNumber, setMemberCardNumber] = useState('');

  // Step 2: Member Verification
  const [matchedMember, setMatchedMember] = useState<IndividualMemberDto | null>(null);
  const [hasUserAccount, setHasUserAccount] = useState(false);

  // Step 3: Account Creation (email/username, password, etc.)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [communicationLanguage, setCommunicationLanguage] = useState<CommunicationLanguage>(CommunicationLanguage.Greek);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [emailVerificationSentTo, setEmailVerificationSentTo] = useState<string | null>(null);

  useEffect(() => {
    if (errorKey) {
      setError(t(errorKey));
    }
  }, [i18n.language, errorKey, t]);

  const clearLookupResult = () => {
    setMatchedMember(null);
    setHasUserAccount(false);
    setError(null);
    setErrorKey(null);
  };

  const handleSearchMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setErrorKey(null);
    setSearching(true);

    try {
      const matchRequest: MatchMemberRequest = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        memberCardNumber: memberCardNumber.trim(),
      };

      const response = await authService.matchMember(matchRequest);

      if (!response.found) {
        setErrorKey('register.memberNotFound');
        setError(t('register.memberNotFound'));
        setMatchedMember(null);
        return;
      }

      if (response.hasUserAccount) {
        setErrorKey('register.memberAlreadyHasAccount');
        setError(t('register.memberAlreadyHasAccount'));
        setHasUserAccount(true);
        setMatchedMember(response.member ?? null);
        return;
      }

      setMatchedMember(response.member ?? null);
      setHasUserAccount(false);
      const memberEmail = response.member?.email ?? '';
      const isSeedEmail = memberEmail.toLowerCase().endsWith('@seed.icom.local');
      setEmail(isSeedEmail ? '' : memberEmail);
      setStep(2);
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : '';
      setErrorKey(null);
      setError(getApiErrorMessage(rawMessage, t, { registration: true }));
      setMatchedMember(null);
    } finally {
      setSearching(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t('register.errors.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('register.errors.passwordMinLength'));
      return;
    }

    if (!acceptedTerms) {
      setError(t('register.errors.acceptTermsRequired'));
      return;
    }

    if (!matchedMember) {
      setError(t('register.errors.memberInfoMissing'));
      return;
    }

    if (!email.trim()) {
      setError(t('register.errors.emailRequired'));
      return;
    }

    setLoading(true);
    setEmailVerificationSentTo(null);
    try {
      const result = await register({
        email: email.trim(),
        password,
        role: UserRole.IndividualMember,
        communicationLanguage,
        registrationType: RegistrationType.ExistingMember,
        existingMemberId: matchedMember.id,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        memberCardNumber: memberCardNumber.trim() || undefined,
      });

      if (result?.requiresEmailConfirmation) {
        showSuccess(t('register.checkEmailConfirmation'));
        setEmailVerificationSentTo(result.email ?? email.trim());
      } else if (result?.token) {
        showSuccess(t('register.registrationSuccessful'));
        navigate('/');
      } else if (result) {
        // Claim flow should always return email confirmation; keep UI on success if token is absent.
        showSuccess(t('register.checkEmailConfirmation'));
        setEmailVerificationSentTo(result.email ?? email.trim());
      }
    } catch (err: unknown) {
      const rawMessage = err instanceof Error ? err.message : '';
      setError(getApiErrorMessage(rawMessage, t, { registration: true }));
    } finally {
      setLoading(false);
    }
  };

  const stepperSteps: Array<{ id: string; label: string; state: StepState }> = [
    { id: '1', label: t('register.memberInformation'), state: step === 1 ? 'active' : step > 1 ? 'completed' : 'pending' },
    { id: '2', label: t('register.memberVerification'), state: step === 2 ? 'active' : step > 2 ? 'completed' : 'pending' },
    { id: '3', label: t('register.accountCreation'), state: step === 3 ? 'active' : 'pending' },
  ];

  useEffect(() => {
    const scrollRoot = document.getElementById('register-form-scroll');
    if (scrollRoot) {
      scrollRoot.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [step]);

  const navActions = (() => {
    if (step === 1) {
      return (
        <>
          <NavSpacer />
          <ButtonGroupRight>
            <Button
              type="submit"
              form="existing-member-search-form"
              variant="primary"
              loading={searching}
              disabled={!firstName.trim() || !lastName.trim() || !memberCardNumber.trim()}
            >
              {t('register.searchMember')}
            </Button>
          </ButtonGroupRight>
        </>
      );
    }
    if (step === 2) {
      return (
        <>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setStep(1);
              setMatchedMember(null);
              setError(null);
            }}
          >
            {t('common.previous')}
          </Button>
          <ButtonGroupRight>
            <Button type="button" variant="primary" onClick={() => setStep(3)}>
              {t('register.continue')} →
            </Button>
          </ButtonGroupRight>
        </>
      );
    }
    if (step === 3 && emailVerificationSentTo) {
      return (
        <>
          <NavSpacer />
          <ButtonGroupRight>
            <Button type="button" variant="primary" onClick={() => navigate('/login')}>
              {t('login.signIn')}
            </Button>
          </ButtonGroupRight>
        </>
      );
    }
    return (
      <>
        <Button type="button" variant="outline" onClick={() => setStep(2)} disabled={loading}>
          {t('common.previous')}
        </Button>
        <ButtonGroupRight>
          <Button
            type="submit"
            form="existing-member-account-form"
            variant="gradient"
            loading={loading}
            disabled={!acceptedTerms || !password || !confirmPassword || !email.trim()}
          >
            {t('register.createAccount')}
          </Button>
        </ButtonGroupRight>
      </>
    );
  })();

  const preferGr = i18n.language?.toLowerCase().startsWith('gr') || i18n.language?.toLowerCase() === 'el';
  const displayLang = preferGr ? 'gr' : 'en';
  const getMembershipTypeLabel = (type: MembershipType): string => {
    const labels: Record<MembershipType, string> = {
      [MembershipType.Regular]: t('membership.regular'),
      [MembershipType.Student]: t('membership.student'),
      [MembershipType.Retired]: t('membership.retired'),
      [MembershipType.Honorary]: t('membership.honorary'),
    };
    return labels[type] ?? String(type);
  };

  return (
    <AuthWizardShell
      title={t('register.registerExistingMember')}
      titleId="register-existing-title"
      backSlot={<BackLink to="/register">← {t('common.back')}</BackLink>}
      footerSlot={navActions}
    >
      <CardWithPadding variant="elevated">
        <Stack direction="column" spacing="md">
          <StepperWrapper>
            <Stepper steps={stepperSteps} ariaLabel={t('register.progressLabel')} />
          </StepperWrapper>

          {error && (
            <ErrorMessage
              variant="filled"
              accentBorder="left"
              role="alert"
              aria-live="assertive"
            >
              <Text size="sm" color="error">{error}</Text>
            </ErrorMessage>
          )}

          <WizardStepPanel stepKey={step} aria-label={stepperSteps[step - 1]?.label}>
            {step === 1 && (
              <Form id="existing-member-search-form" onSubmit={handleSearchMember}>
                <FormSection
                  title={t('register.memberInformation')}
                  description={t('register.memberInformationIntro')}
                  spacing="sm"
                >
                  <Stack direction="column" spacing="sm">
                    <FormField
                      label={t('register.firstNameGreekOrEnglish')}
                      type="text"
                      name="firstName"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={(e) => {
                        setFirstName(e.target.value);
                        clearLookupResult();
                      }}
                      placeholder={t('register.firstNamePlaceholder')}
                      required
                      disabled={searching}
                    />
                    <FormField
                      label={t('register.lastNameGreekOrEnglish')}
                      type="text"
                      name="lastName"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={(e) => {
                        setLastName(e.target.value);
                        clearLookupResult();
                      }}
                      placeholder={t('register.lastNamePlaceholder')}
                      required
                      disabled={searching}
                    />
                    <FormField
                      label={t('register.memberCardNumber')}
                      type="text"
                      value={memberCardNumber}
                      onChange={(e) => {
                        setMemberCardNumber(e.target.value);
                        clearLookupResult();
                      }}
                      required
                      disabled={searching}
                      helperText={t('register.memberCardNumberHint')}
                    />
                  </Stack>
                </FormSection>
              </Form>
            )}

            {step === 2 && matchedMember && (
              <div>
                <SuccessMessage variant="filled" accentBorder="left">
                  <TextWithMargin size="sm" color="success" weight="semibold">
                    {t('register.memberFound')}
                  </TextWithMargin>
                  <Text size="sm" color="success">
                    {t('register.memberFoundDescription')}
                  </Text>
                </SuccessMessage>

                <MemberInfoCard variant="filled" accentBorder="left">
                  <Stack direction="column" spacing="md">
                    <div>
                      <TextUppercase size="xs" color="secondary" weight="semibold">
                        {t('register.memberName')}
                      </TextUppercase>
                      <Heading variant="h4" color="text">
                        {getMemberDisplayFirstName(matchedMember, displayLang)}{' '}
                        {getMemberDisplayLastName(matchedMember, displayLang)}
                      </Heading>
                    </div>
                    {matchedMember.memberCardNumber != null && matchedMember.memberCardNumber !== '' && (
                      <div>
                        <TextUppercase size="xs" color="secondary" weight="semibold">
                          {t('register.memberCardNumber')}
                        </TextUppercase>
                        <Text size="base" color="text">{matchedMember.memberCardNumber}</Text>
                      </div>
                    )}
                    {matchedMember.dateOfBirth && (
                      <div>
                        <Text size="xs" color="secondary" weight="semibold" style={{ letterSpacing: '0.05em' }}>
                          {t('register.dateOfBirth')}
                        </Text>
                        <Text size="base" color="text">
                          {new Date(matchedMember.dateOfBirth).toLocaleDateString()}
                        </Text>
                      </div>
                    )}
                    {matchedMember.membershipType != null && (
                      <div>
                        <TextUppercase size="xs" color="secondary" weight="semibold">
                          {t('register.membershipType')}
                        </TextUppercase>
                        <Badge size="sm" color="primary" shape="pill">
                          {getMembershipTypeLabel(matchedMember.membershipType as MembershipType)}
                        </Badge>
                      </div>
                    )}
                  </Stack>
                </MemberInfoCard>
              </div>
            )}

            {step === 3 && emailVerificationSentTo && (
              <div>
                <SuccessMessage variant="filled" accentBorder="left">
                  <TextWithMargin size="sm" color="success" weight="semibold">
                    {t('register.checkEmailConfirmation')}
                  </TextWithMargin>
                  <Text size="sm" color="success">
                    {t('register.confirmationEmailSentTo')} {emailVerificationSentTo}
                  </Text>
                </SuccessMessage>
              </div>
            )}

            {step === 3 && !emailVerificationSentTo && (
              <Form id="existing-member-account-form" onSubmit={handleSubmit}>
                <FormSection title={t('register.accountCreation')}>
                  <Stack direction="column" spacing="md">
                    <FormField
                      id="existing-member-email"
                      label={t('common.email')}
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={loading}
                      helperText={
                        matchedMember?.email?.toLowerCase().endsWith('@seed.icom.local')
                          ? t('register.enterRealEmail')
                          : undefined
                      }
                    />
                    <FormField
                      id="existing-member-password"
                      label={t('register.password')}
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                      helperText={t('register.passwordHint')}
                      iconRight={
                        <PasswordVisibilityToggle
                          visible={showPassword}
                          onToggle={() => setShowPassword(v => !v)}
                          controlsId="existing-member-password"
                        />
                      }
                    />
                    <FormField
                      id="existing-member-confirm-password"
                      label={t('register.confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      iconRight={
                        <PasswordVisibilityToggle
                          visible={showConfirmPassword}
                          onToggle={() => setShowConfirmPassword(v => !v)}
                          controlsId="existing-member-confirm-password"
                        />
                      }
                    />
                    <FormField
                      label={t('register.communicationLanguage')}
                      select
                      options={[
                        { value: CommunicationLanguage.English, label: t('register.languageEnglish') },
                        { value: CommunicationLanguage.Greek, label: t('register.languageGreek') },
                        { value: CommunicationLanguage.French, label: t('register.languageFrench') },
                        { value: CommunicationLanguage.Spanish, label: t('register.languageSpanish') },
                      ]}
                      value={communicationLanguage}
                      onChange={(e) => setCommunicationLanguage(e.target.value as CommunicationLanguage)}
                      disabled={loading}
                    />
                    <CheckboxContainer>
                      <Checkbox
                        type="checkbox"
                        id="terms"
                        checked={acceptedTerms}
                        onChange={(e) => setAcceptedTerms(e.target.checked)}
                        disabled={loading}
                      />
                      <CheckboxLabel htmlFor="terms">{t('register.acceptTerms')}</CheckboxLabel>
                    </CheckboxContainer>
                  </Stack>
                </FormSection>
              </Form>
            )}
          </WizardStepPanel>
        </Stack>
      </CardWithPadding>
    </AuthWizardShell>
  );
};

export default RegisterExistingMember;
